import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Package, MapPin, Tag, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { formatLocationPath } from "@/lib/locationPath";

// Debounced inline search across items, locations, and categories.
// Renders results as a dropdown directly under the input.
export default function DashboardSearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ items: [], locations: [], categories: [] });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) {
      setResults({ items: [], locations: [], categories: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const term = q.trim();
        const [itemsRes, locsRes, catsRes] = await Promise.all([
          api.get("/items", { params: { q: term, limit: 8 } }).catch(() => ({ data: [] })),
          api.get("/locations").catch(() => ({ data: [] })),
          api.get("/categories").catch(() => ({ data: [] })),
        ]);
        const t = term.toLowerCase();
        const locs = (locsRes.data || []).filter((l) => (l.name || "").toLowerCase().includes(t)).slice(0, 5);
        const cats = (catsRes.data || []).filter((c) => (c.name || "").toLowerCase().includes(t)).slice(0, 5);
        setResults({ items: itemsRes.data || [], locations: locs, categories: cats, allLocations: locsRes.data || [] });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const totalCount =
    (results.items?.length || 0) + (results.locations?.length || 0) + (results.categories?.length || 0);
  const showDropdown = open && q.trim().length > 0;

  const goSearch = () => {
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="dashboard-search-bar">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          goSearch();
        }}
        className="relative"
      >
        <MagnifyingGlass
          weight="bold"
          size={20}
          className="absolute left-5 top-1/2 -translate-y-1/2 text-stoneink-500 pointer-events-none"
        />
        <input
          data-testid="dashboard-search-input"
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q.trim() && setOpen(true)}
          placeholder="Search items, locations, or categories…"
          className="w-full h-14 pl-14 pr-12 rounded-2xl border border-sand-300 bg-white shadow-ambient text-stoneink-900 placeholder:text-stoneink-500 focus:outline-none focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/15 transition-colors"
        />
        {q && (
          <button
            type="button"
            data-testid="dashboard-search-clear"
            onClick={() => {
              setQ("");
              setOpen(false);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-full text-stoneink-500 hover:text-stoneink-900 hover:bg-sand-200"
            aria-label="Clear search"
          >
            <X size={16} weight="bold" />
          </button>
        )}
      </form>

      {showDropdown && (
        <div
          data-testid="dashboard-search-results"
          className="absolute z-30 left-0 right-0 mt-2 rounded-2xl border border-sand-300 bg-white shadow-hover overflow-hidden"
        >
          {loading && totalCount === 0 ? (
            <div className="px-5 py-6 text-sm text-stoneink-500">Searching…</div>
          ) : totalCount === 0 ? (
            <div className="px-5 py-6 text-sm text-stoneink-500" data-testid="dashboard-search-empty">
              Nothing matches “{q}”. Try a shorter word or a tag.
            </div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto divide-y divide-sand-200">
              {results.items?.length > 0 && (
                <SearchGroup
                  title="Items"
                  Icon={Package}
                  results={results.items}
                  renderItem={(it) => {
                    const path = formatLocationPath(results.allLocations || [], it.location_id);
                    return (
                    <button
                      key={it.id}
                      data-testid={`search-result-item-${it.id}`}
                      onClick={() => {
                        navigate(`/items/${it.id}`);
                        setOpen(false);
                      }}
                      className="w-full text-left px-5 py-3 hover:bg-sand-100 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-stoneink-900 font-medium truncate">{it.name}</div>
                        <div className="text-xs text-stoneink-500 truncate">
                          {path ? `in ${path}` : "No location"}
                          {it.tags?.length > 0 && <span> · {it.tags.slice(0, 2).join(", ")}</span>}
                        </div>
                      </div>
                      <span className="text-xs text-terracotta-600 shrink-0">Open →</span>
                    </button>
                  );}}
                />
              )}
              {results.locations?.length > 0 && (
                <SearchGroup
                  title="Locations"
                  Icon={MapPin}
                  results={results.locations}
                  renderItem={(l) => (
                    <button
                      key={l.id}
                      data-testid={`search-result-location-${l.id}`}
                      onClick={() => {
                        navigate(`/locations`);
                        setOpen(false);
                      }}
                      className="w-full text-left px-5 py-3 hover:bg-sand-100 flex items-center justify-between gap-3"
                    >
                      <div className="text-stoneink-900 font-medium truncate">{l.name}</div>
                      <span className="text-xs text-terracotta-600 shrink-0">View →</span>
                    </button>
                  )}
                />
              )}
              {results.categories?.length > 0 && (
                <SearchGroup
                  title="Categories"
                  Icon={Tag}
                  results={results.categories}
                  renderItem={(c) => (
                    <button
                      key={c.id}
                      data-testid={`search-result-category-${c.id}`}
                      onClick={() => {
                        navigate(`/categories`);
                        setOpen(false);
                      }}
                      className="w-full text-left px-5 py-3 hover:bg-sand-100 flex items-center justify-between gap-3"
                    >
                      <div className="text-stoneink-900 font-medium truncate">{c.name}</div>
                      <span className="text-xs text-terracotta-600 shrink-0">View →</span>
                    </button>
                  )}
                />
              )}
              <button
                onClick={goSearch}
                data-testid="dashboard-search-see-all"
                className="w-full text-left px-5 py-3 text-sm text-terracotta-600 hover:bg-sand-100 font-medium"
              >
                See all results for “{q}” →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const SearchGroup = ({ title, Icon, results, renderItem }) => (
  <div>
    <div className="flex items-center gap-2 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-stoneink-500 bg-sand-100/60">
      <Icon weight="duotone" size={14} /> {title} · {results.length}
    </div>
    {results.map(renderItem)}
  </div>
);
